import { SplashScreen } from '@/components/loading-screen';
import { useUserProfile } from '@/hooks/useUsers';
import { useXpanseAuth } from '@/hooks/useXpanseAuthentication';
import { Auth0Provider } from '@auth0/auth0-react';
import { FullStory, init as initFullStory } from '@fullstory/browser';
import { Suspense, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import SessionTimeoutModal from './SessionTimeoutModal';

const AuthConsumer = ({ children }: { children: React.ReactNode }) => {

  const isIce = window.location.href.indexOf('/embeddable/ice') > -1;
  const isMLink = window.location.href.indexOf('/embeddable/mlink') > -1;

  const { error, isAuthenticated, isLoading, loginWithRedirect, user, logout } = useXpanseAuth();

  const navigate = useNavigate();

  const authenticate = useCallback(async () => {
    if (!isLoading && !user && !isIce && !isMLink) {
      await loginWithRedirect();
    } else if (user) {
      const {
        nickName: displayName,
        email,
        organizationName,
      } = user?.['https://xpanse.com/claims/auth/context'] ?? {};

      const fullstoryOrgId = import.meta.env.VITE_FULLSTORY_ORG_ID;

      if (fullstoryOrgId) {
        initFullStory({ orgId: fullstoryOrgId });

        FullStory('setIdentity', {
          // omit 'auth0|'
          uid: user.sub?.split('|')[1],
          properties: {
            displayName,
            email,
            org: organizationName,
          },
        });
      }
    }
  }, [isLoading, loginWithRedirect, user, isIce, isMLink]);

  useEffect(() => {
    if (!error && error === 'user_in_denied_list') {
      navigate('/forbidden');
    } else {
      authenticate();
    }
  }, [authenticate, error, isAuthenticated, navigate]);

  const userProfile = useUserProfile();

  return (
    <>
      <SessionTimeoutModal
        sessionDuration={14400}
        showModalAt={120}
        onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        scope={userProfile?.scope}
      />
      {isLoading && <SplashScreen />}
      {(isAuthenticated || isIce || isMLink) && <>{children}</>}
    </>
  );
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => (
  <Auth0Provider
    domain={import.meta.env.VITE_AUTH0_DOMAIN}
    cacheLocation="localstorage"
    clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
    authorizationParams={{
      redirect_uri: window.location.origin,
      scope: 'openid profile email refresh offline_access',
    }}
  >
    <Suspense fallback={<SplashScreen />}>
      <AuthConsumer>{children}</AuthConsumer>
    </Suspense>
  </Auth0Provider>
);
